import {
  addShopifyCartLines,
  createShopifyCart,
  getShopifyCartById,
  removeShopifyCartLines,
  updateShopifyCartBuyerIdentity
} from '@portal/api/shopifyV2/cart';
import {
  CartAddItemInput,
  CartBuyerIdentityInput,
  CartLineItem,
  CartUpdateInput
} from '@portal/api/shopifyV2/types';
import { StoreCode } from '@portal/schema/storeCode';
import { CartRules } from '@portal/shared/utils/cartRules';
import { safeLocalStorage } from '@portal/shared/utils/safeLocalStorage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ShopV3CartStoreState } from './types';
import { setCartError, setCartLoading } from './utils';

// Global instance of cart rules processor with caching
// Note: Cart rules are used in both addToCart and updateCart to ensure consistent behavior
const cartRules = new CartRules();

// Helper function to update cart state with loading completion
const updateCartState = (set: any, cart: any, isLoading = false) => {
  set((state: any) => {
    const newState = { ...state };
    newState.data.cart = cart;
    newState.loading.isCartUpdating = isLoading;
    return newState;
  });
};

export const useShopV3CartStore = create<ShopV3CartStoreState>()(
  devtools((set, get) => ({
    data: {
      cart: null,
      isOpen: false
    },
    loading: {
      isCartLoading: false,
      isCartUpdating: false
    },
    errors: {
      cartError: null,
      quantityErrors: []
    },
    actions: {
      openCart: () =>
        set(
          (state) => ({ ...state, data: { ...state.data, isOpen: true } }),
          undefined,
          'openCart'
        ),
      closeCart: () =>
        set(
          (state) => ({ ...state, data: { ...state.data, isOpen: false } }),
          undefined,
          'closeCart'
        ),
      setCartOpen: (isOpen: boolean) =>
        set((state) => ({ ...state, data: { ...state.data, isOpen } }), undefined, 'setCartOpen'),
      fetchCartData: async (store: StoreCode, id: string) => {
        set((state) => setCartLoading(state, 'isCartLoading', true));
        try {
          const cartData = await getShopifyCartById(store, id);
          set((state) => {
            const newState = { ...state };
            newState.data.cart = cartData;
            newState.loading.isCartLoading = false;
            return newState;
          });
          return cartData;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
          return null;
        } finally {
          set((state) => setCartLoading(state, 'isCartLoading', false));
        }
      },
      fetchCartDataInternal: (store: StoreCode) => {
        const currentCartId = get().data.cart?.id;
        if (!currentCartId) return;
        get().actions.fetchCartData(store, currentCartId);
      },
      addToCart: async (store: StoreCode, cartInput: CartAddItemInput, customerTags?: string[]) => {
        set((state) => setCartLoading(state, 'isCartUpdating', true));
        try {
          const shouldCreateCart = !cartInput.cartId;

          if (shouldCreateCart) {
            // Create new cart with client-side cart rules

            // Process cart rules
            const { processedLines, quantityErrors } = await cartRules.processCartRules(
              store,
              cartInput.lines,
              customerTags
            );
            if (quantityErrors.length > 0) {
              // Set quantity errors in store
              get().actions.setQuantityErrors(quantityErrors);
            } else {
              // Clear any existing quantity errors
              get().actions.clearQuantityErrors();
            }

            // Create cart with processed lines
            const { cartId: _cartId, ...cartInputForCreation } = cartInput;
            const modifiedCartInput = {
              ...cartInputForCreation,
              lines: processedLines
            };

            const createdCart = await createShopifyCart(store, modifiedCartInput);

            // Store cart ID
            safeLocalStorage.setItem('cartId', createdCart.id);

            // Get full cart details
            const cart = await getShopifyCartById(store, createdCart.id);

            // Update state
            updateCartState(set, cart);

            return cart;
          }

          // Add to existing cart with client-side cart rules

          // Get current cart state
          const currentCart = await getShopifyCartById(store, cartInput.cartId);
          const allCurrentCartLines: CartLineItem[] = currentCart.lines.map((line) => ({
            merchandiseId: line.merchandise.id,
            quantity: line.quantity,
            // Extract sellingPlanId from sellingPlanAllocation
            sellingPlanId: line.sellingPlanAllocation?.sellingPlan?.id,
            attributes: line.attributes
          }));

          // Separate purchased items from freebies in the current cart
          // This prevents old freebies from being treated as purchased items
          const { purchasedItems: currentPurchasedItems } =
            await cartRules.separatePurchasedFromFreebies(store, allCurrentCartLines);

          // Combine current purchased items with new lines BEFORE processing rules
          // This ensures that adding an item that already exists behaves like increasing quantity
          const allLines = [...currentPurchasedItems, ...cartInput.lines];
          const consolidatedLinesBeforeRules = CartRules.consolidateCartLines(allLines);

          // Process the consolidated lines with cart rules
          const { processedLines, quantityErrors } = await cartRules.processCartRules(
            store,
            consolidatedLinesBeforeRules,
            customerTags
          );
          if (quantityErrors.length > 0) {
            // Set quantity errors in store
            get().actions.setQuantityErrors(quantityErrors);
          } else {
            // Clear any existing quantity errors
            get().actions.clearQuantityErrors();
          }

          // Clear existing cart and add all items
          if (currentCart.lines.length > 0) {
            const lineIdsToRemove = currentCart.lines.map((line) => line.id);
            await removeShopifyCartLines(store, {
              cartId: cartInput.cartId,
              lineIds: lineIdsToRemove
            });
          }

          // Add all consolidated items
          const cart = await addShopifyCartLines(store, {
            cartId: cartInput.cartId,
            lines: processedLines.filter(
              (line: any) => line.merchandiseId && line.quantity && line.quantity > 0
            )
          });

          // Update state
          updateCartState(set, cart);

          return cart;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
          return null;
        } finally {
          set((state) => setCartLoading(state, 'isCartUpdating', false));
        }
      },
      updateCart: async (store: StoreCode, cartInput: CartUpdateInput) => {
        set((state) => setCartLoading(state, 'isCartUpdating', true));
        try {
          // Get current cart to understand what we're updating
          const currentCart = await getShopifyCartById(store, cartInput.cartId);
          const currentCartLines: CartLineItem[] = currentCart.lines.map((line) => ({
            merchandiseId: line.merchandise.id,
            quantity: line.quantity,
            // Extract sellingPlanId from sellingPlanAllocation
            sellingPlanId: line.sellingPlanAllocation?.sellingPlan?.id,
            attributes: line.attributes
          }));

          // Convert line ID updates to full CartLineItem format for cart rules processing
          // Also track which existing lines are being replaced (merchandise ID changes)
          const updateLinesByMerchandiseId: CartLineItem[] = [];
          const replacedLineIds = new Set<string>();

          cartInput.lines.forEach((line) => {
            if ('id' in line && line.quantity !== undefined) {
              // Find the current cart line to get merchandise info
              const currentLine = currentCart.lines.find((cl) => cl.id === line.id);
              if (currentLine) {
                const updateMerchandiseId = line.merchandiseId || currentLine.merchandise.id;

                // If merchandise ID is changing, mark the original line as replaced
                if (line.merchandiseId && line.merchandiseId !== currentLine.merchandise.id) {
                  replacedLineIds.add(line.id);
                }

                updateLinesByMerchandiseId.push({
                  merchandiseId: updateMerchandiseId,
                  quantity: line.quantity,
                  // Preserve sellingPlanId from update input or existing line
                  sellingPlanId:
                    line.sellingPlanId || currentLine.sellingPlanAllocation?.sellingPlan?.id,
                  // Preserve attributes from update input or existing line
                  attributes: line.attributes || currentLine.attributes
                });
              }
            }
          });

          // Filter out existing cart lines that are being replaced
          const filteredCurrentCartLines = currentCartLines.filter((line, index) => {
            const originalLineId = currentCart.lines[index]?.id;
            return originalLineId ? !replacedLineIds.has(originalLineId) : true;
          });

          // Apply cart rules to the update
          const { itemsToAdd, quantityErrors } = await cartRules.processCartUpdatesWithRules(
            store,
            updateLinesByMerchandiseId,
            filteredCurrentCartLines
          );

          // Set or clear quantity errors
          if (quantityErrors.length > 0) {
            get().actions.setQuantityErrors(quantityErrors);
          } else {
            get().actions.clearQuantityErrors();
          }

          // Now we need to intelligently apply the changes
          // Instead of remove-all + add-all, we'll:
          // 1. Remove all existing lines
          // 2. Add the processed lines
          // This is still more efficient than the old approach which did additional processing

          let updatedCart = currentCart;

          // Remove all existing cart lines
          if (currentCart.lines.length > 0) {
            const lineIdsToRemove = currentCart.lines.map((line) => line.id);
            updatedCart = await removeShopifyCartLines(store, {
              cartId: cartInput.cartId,
              lineIds: lineIdsToRemove
            });
          }

          // Add the processed items (includes both updated items and any freebies/swaps from cart rules)
          if (itemsToAdd.length > 0) {
            updatedCart = await addShopifyCartLines(store, {
              cartId: cartInput.cartId,
              lines: itemsToAdd.filter(
                (line: CartLineItem) => line.merchandiseId && line.quantity && line.quantity > 0
              )
            });
          }

          // Update state
          updateCartState(set, updatedCart);

          return updatedCart;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
          return null;
        } finally {
          set((state) => setCartLoading(state, 'isCartUpdating', false));
        }
      },
      updateCartBuyerIdentity: async (
        store: StoreCode,
        buyerIdentityInput: CartBuyerIdentityInput
      ) => {
        set((state) => setCartLoading(state, 'isCartUpdating', true));
        try {
          const cart = await updateShopifyCartBuyerIdentity(store, buyerIdentityInput);

          const { id } = cart;
          if (!id) throw new Error('Update cart buyer identity response missing ID');

          // Always update the cart state with fresh data
          updateCartState(set, cart);

          return cart;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
          return null;
        } finally {
          set((state) => setCartLoading(state, 'isCartUpdating', false));
        }
      },
      clearCart: async (store: StoreCode) => {
        set((state) => setCartLoading(state, 'isCartUpdating', true));
        try {
          const currentCart = get().data.cart;
          if (!currentCart || currentCart.lines.length === 0) {
            // No cart or already empty
            return currentCart;
          }

          // Remove all line items from the cart
          const lineIdsToRemove = currentCart.lines.map((line) => line.id);
          const clearedCart = await removeShopifyCartLines(store, {
            cartId: currentCart.id,
            lineIds: lineIdsToRemove
          });

          // Update state with empty cart
          updateCartState(set, clearedCart);

          return clearedCart;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
          return null;
        } finally {
          set((state) => setCartLoading(state, 'isCartUpdating', false));
        }
      },
      deleteCart: () => {
        set((state) => {
          const newState = { ...state };
          newState.data.cart = null;
          newState.data.isOpen = false;
          newState.loading.isCartLoading = false;
          newState.loading.isCartUpdating = false;
          newState.errors.cartError = null;
          return newState;
        });
        safeLocalStorage.removeItem('cartId');
      },
      // Wrap custom submit function with loading state
      submitCart: async (submitFunction: () => void) => {
        set((state) => setCartLoading(state, 'isCartUpdating', true));
        try {
          await submitFunction();
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Store - An unknown error occurred');
          set((state) => setCartError(state, 'cartError', error));
        } finally {
          set((state) => setCartLoading(state, 'isCartUpdating', false));
        }
      },
      setQuantityErrors: (
        errors: Array<{
          merchandiseId: string;
          requestedQuantity: number;
          cappedQuantity: number;
          errorMessage: string;
        }>
      ) => {
        set((state) => ({
          ...state,
          errors: {
            ...state.errors,
            quantityErrors: errors
          }
        }));
      },
      clearQuantityErrors: () => {
        set((state) => ({
          ...state,
          errors: {
            ...state.errors,
            quantityErrors: []
          }
        }));
      }
    }
  }))
);
